/**
 * Canonical orgLevel <-> examLevel guardrail, shared by backend and frontend.
 * Primary (P1-P6) forces PSLE; secondary (Sec 1-4) requires one of
 * G1/G2/G3/IP/IB. Lives in `shared/` so the class-code contract
 * (`shared/classCodeContract.ts`) and the server access-code validation can
 * both self-validate against a single source of truth without `shared/`
 * importing `server/`.
 */

import {
  EXAM_LEVELS,
  type ExamLevel,
  SECONDARY_EXAM_LEVELS,
} from '../examLevels.js';
import { ORG_LEVELS } from '../orgLevels.js';

const PRIMARY_ORG_LEVELS: readonly string[] = ORG_LEVELS.filter(l =>
  l.startsWith('primary_')
);
const SECONDARY_ORG_LEVELS: readonly string[] = ORG_LEVELS.filter(l =>
  l.startsWith('secondary_')
);

export { SECONDARY_EXAM_LEVELS };

export function getDefaultExamLevelForOrgLevel(
  orgLevel: string | null | undefined
): ExamLevel | null {
  if (!orgLevel || typeof orgLevel !== 'string') return null;
  const normalized = orgLevel.trim();
  return PRIMARY_ORG_LEVELS.includes(normalized) ? 'PSLE' : null;
}

export function isPrimaryOrgLevel(
  orgLevel: string | null | undefined
): boolean {
  return (
    Boolean(orgLevel) &&
    typeof orgLevel === 'string' &&
    PRIMARY_ORG_LEVELS.includes(orgLevel.trim())
  );
}

export function isSecondaryOrgLevel(
  orgLevel: string | null | undefined
): boolean {
  return (
    Boolean(orgLevel) &&
    typeof orgLevel === 'string' &&
    SECONDARY_ORG_LEVELS.includes(orgLevel.trim())
  );
}

export function validateAccessCodeLevels(
  orgLevel: string | null | undefined,
  examLevel: string | null | undefined
): { orgLevel: string; examLevel: string | null } {
  const trimmedOrg = orgLevel != null ? String(orgLevel).trim() : '';
  if (!(ORG_LEVELS as readonly string[]).includes(trimmedOrg)) {
    throw new Error(
      `Invalid orgLevel: "${orgLevel}". Must be one of: ${ORG_LEVELS.join(', ')}`
    );
  }

  if (isPrimaryOrgLevel(trimmedOrg)) {
    return { orgLevel: trimmedOrg, examLevel: 'PSLE' };
  }

  if (isSecondaryOrgLevel(trimmedOrg)) {
    const trimmedExam =
      examLevel != null && examLevel !== '' ? String(examLevel).trim() : null;
    if (
      !trimmedExam ||
      !(SECONDARY_EXAM_LEVELS as readonly string[]).includes(trimmedExam)
    ) {
      throw new Error(
        `For secondary org level (${trimmedOrg}), examLevel must be one of: ${SECONDARY_EXAM_LEVELS.join(', ')}`
      );
    }
    return { orgLevel: trimmedOrg, examLevel: trimmedExam };
  }

  const trimmedExam =
    examLevel != null && examLevel !== '' ? String(examLevel).trim() : null;
  if (
    trimmedExam &&
    !(EXAM_LEVELS as readonly string[]).includes(trimmedExam)
  ) {
    throw new Error(
      `Invalid examLevel: "${examLevel}". Must be one of: ${EXAM_LEVELS.join(', ')}`
    );
  }
  return { orgLevel: trimmedOrg, examLevel: trimmedExam };
}
